<template>
  <div class="cms-gallery-block">
    <header v-if="block.title || block.subtitle" class="cms-gallery-header">
      <h3 v-if="block.title" class="cms-gallery-title">{{ block.title }}</h3>
      <p v-if="block.subtitle" class="cms-gallery-subtitle">{{ block.subtitle }}</p>
    </header>

    <div v-if="items.length" class="cms-gallery-grid">
      <button
        v-for="(item, index) in items"
        :key="`${item.url}-${index}`"
        type="button"
        class="cms-gallery-item"
        @click="openLightbox(index)"
      >
        <img :src="resolveUrl(item.url)" :alt="item.title || block.title || '图集图片'" />
        <span v-if="item.title" class="cms-gallery-item-title">{{ item.title }}</span>
      </button>
    </div>
    <p v-else class="cms-gallery-empty">暂无图片</p>

    <div v-if="lightboxIndex !== null" class="cms-gallery-lightbox" @click="closeLightbox">
      <div class="cms-gallery-lightbox-inner" @click.stop>
        <button type="button" class="cms-gallery-lightbox-close" @click="closeLightbox">×</button>
        <img :src="resolveUrl(activeItem.url)" :alt="activeItem.title || '放大查看'" />
        <p v-if="activeItem.title" class="cms-gallery-lightbox-caption">{{ activeItem.title }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GalleryBlock',
  props: {
    block: {
      type: Object,
      required: true
    }
  },
  inject: {
    front: {
      default: null
    }
  },
  data() {
    return {
      lightboxIndex: null
    }
  },
  computed: {
    items() {
      return this.parseGalleryItems(this.block.bodyContent)
    },
    activeItem() {
      if (this.lightboxIndex === null || !this.items[this.lightboxIndex]) {
        return { url: '', title: '' }
      }
      return this.items[this.lightboxIndex]
    }
  },
  methods: {
    parseGalleryItems(content) {
      if (!content) {
        return []
      }

      const trimmed = String(content).trim()
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed)
          const list = Array.isArray(parsed) ? parsed : [parsed]
          return list
            .map(item => ({
              url: item.url || item.src || item.image || '',
              title: item.title || item.caption || item.name || ''
            }))
            .filter(item => item.url)
        } catch (error) {
          // fall through to HTML parsing
        }
      }

      const items = []
      const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi
      let match = imgRegex.exec(content)
      while (match) {
        const tag = match[0]
        const altMatch = tag.match(/alt=["']([^"']*)["']/i)
        const titleMatch = tag.match(/title=["']([^"']*)["']/i)
        items.push({
          url: match[1],
          title: (altMatch && altMatch[1]) || (titleMatch && titleMatch[1]) || ''
        })
        match = imgRegex.exec(content)
      }
      return items
    },
    resolveUrl(url) {
      if (this.front && typeof this.front.resolveFileUrl === 'function') {
        return this.front.resolveFileUrl(url)
      }
      return url
    },
    openLightbox(index) {
      this.lightboxIndex = index
    },
    closeLightbox() {
      this.lightboxIndex = null
    }
  }
}
</script>

<style scoped>
.cms-gallery-header {
  margin-bottom: 16px;
}

.cms-gallery-title {
  margin: 0 0 6px;
  color: var(--primary);
  font-size: 20px;
}

.cms-gallery-subtitle {
  margin: 0;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.6;
}

.cms-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
}

.cms-gallery-item {
  display: grid;
  gap: 8px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.cms-gallery-item:hover {
  border-color: var(--gold);
}

.cms-gallery-item img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.cms-gallery-item-title {
  padding: 0 10px 10px;
  color: var(--subtle);
  font-size: 12px;
  line-height: 1.5;
}

.cms-gallery-empty {
  margin: 0;
  color: var(--subtle);
  font-size: 14px;
}

.cms-gallery-lightbox {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 20, 40, 0.82);
}

.cms-gallery-lightbox-inner {
  position: relative;
  max-width: min(960px, 100%);
  max-height: 90vh;
}

.cms-gallery-lightbox-inner img {
  display: block;
  max-width: 100%;
  max-height: calc(90vh - 48px);
  margin: 0 auto;
  border-radius: var(--front-radius);
}

.cms-gallery-lightbox-close {
  position: absolute;
  top: -36px;
  right: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
}

.cms-gallery-lightbox-caption {
  margin: 12px 0 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  text-align: center;
}
</style>
